import * as THREE from 'three';
import { TrackElement } from './TrackElement';
import { TrainingTrackElement } from '../../../../../shared/src/trainingTrack.types';
import { TRAINING_TRACK_CONFIG } from '../config/TrainingTrackConfig';

/**
 * Class representing a booster element in the training track
 */
export class BoosterElement extends TrackElement {
    private boosterMesh: THREE.Mesh | null = null;
    private particles: THREE.Points | null = null;
    private particlePositions: Float32Array | null = null;
    private boostStrength: number;
    private boostDuration: number;
    private cooldown: boolean = false;
    
    /**
     * Constructor
     * @param element The booster element data
     */
    constructor(element: TrainingTrackElement) {
        super(element);
        this.boostStrength = element.properties?.boostStrength || 1.5;
        this.boostDuration = element.properties?.boostDuration || 3.0;
    }
    
    /**
     * Creates the Three.js object for this booster
     * @returns The created booster object
     */
    protected createObject(): THREE.Object3D {
        // Create a rectangular pad for the booster
        const geometry = new THREE.BoxGeometry(this.element.scale * 2, 0.1, this.element.scale * 4);
        const material = new THREE.MeshStandardMaterial({
            color: TRAINING_TRACK_CONFIG.BOOSTER.COLOR,
            emissive: TRAINING_TRACK_CONFIG.BOOSTER.COLOR,
            emissiveIntensity: 0.5
        });
        
        this.boosterMesh = new THREE.Mesh(geometry, material);
        this.boosterMesh.receiveShadow = true;
        
        // Create particle effect for the booster
        const particleCount = TRAINING_TRACK_CONFIG.BOOSTER.PARTICLE_COUNT;
        const particleGeometry = new THREE.BufferGeometry();
        const particleMaterial = new THREE.PointsMaterial({
            color: TRAINING_TRACK_CONFIG.BOOSTER.COLOR,
            size: TRAINING_TRACK_CONFIG.BOOSTER.PARTICLE_SIZE,
            transparent: true,
            blending: THREE.AdditiveBlending
        });
        
        // Create particle positions along the booster pad
        this.particlePositions = new Float32Array(particleCount * 3);
        for (let i = 0; i < particleCount; i++) {
            this.particlePositions[i * 3] = (Math.random() - 0.5) * this.element.scale * 1.8;
            this.particlePositions[i * 3 + 1] = Math.random() * 0.5;
            this.particlePositions[i * 3 + 2] = (Math.random() - 0.5) * this.element.scale * 3.8;
        }
        
        particleGeometry.setAttribute('position', new THREE.BufferAttribute(this.particlePositions, 3));
        this.particles = new THREE.Points(particleGeometry, particleMaterial);
        
        // Create a group to hold both the booster and particles
        const group = new THREE.Group();
        group.add(this.boosterMesh);
        group.add(this.particles);
        
        return group;
    }
    
    /**
     * Updates the booster animation
     * @param deltaTime Time elapsed since last update in seconds
     */
    public update(deltaTime: number): void {
        if (!this.particles || !this.particlePositions) return;
        
        const particleCount = TRAINING_TRACK_CONFIG.BOOSTER.PARTICLE_COUNT;
        
        for (let i = 0; i < particleCount; i++) {
            // Make particles move upward and reset when they get too high
            this.particlePositions[i * 3 + 1] += Math.random() * 0.1 * deltaTime * 10;
            
            if (this.particlePositions[i * 3 + 1] > 0.5) {
                this.particlePositions[i * 3 + 1] = 0;
            }
        }
        
        this.particles.geometry.attributes.position.needsUpdate = true;
    }
    
    /**
     * Handles player interaction with this booster
     * @param playerPosition The player's position
     * @param playerTank The player's tank
     * @param distance The distance to the player
     * @returns True if interaction was handled, false otherwise
     */
    public handleInteraction(playerPosition: THREE.Vector3, playerTank: any, distance: number): boolean {
        if (this.cooldown) {
            console.log('[Booster] Still in cooldown');
            return false;
        }
        
        // Check if player is on the booster
        if (distance < this.element.scale * 2) {
            // Apply boost to player's tank if it has the applyBoost method
            if (playerTank.applyBoost) {
                console.log('[Booster] Applying speed boost to player', {
                    strength: this.boostStrength,
                    duration: this.boostDuration
                });
                
                playerTank.applyBoost(this.boostStrength, this.boostDuration * 1000);
                
                // Set cooldown to prevent multiple activations
                this.cooldown = true;
                setTimeout(() => {
                    this.cooldown = false;
                    console.log('[Booster] Cooldown finished');
                }, 2000);
                
                return true;
            } else {
                console.log('[Booster] Player tank does not have applyBoost method');
            }
        }
        
        return false;
    }
    
    /**
     * Gets the boost strength
     * @returns The boost strength
     */
    public getBoostStrength(): number {
        return this.boostStrength;
    }
    
    /**
     * Gets the boost duration
     * @returns The boost duration in seconds
     */
    public getBoostDuration(): number {
        return this.boostDuration;
    }
    
    /**
     * Checks if the booster is in cooldown
     * @returns True if the booster is in cooldown, false otherwise
     */
    public isInCooldown(): boolean {
        return this.cooldown;
    }
}